import { apiErrorText } from '@/api/errors';
import EmptyState from '@/components/EmptyState';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import {
  DAY_I18N_KEY,
  WEEK,
  WeeklyHoursEditor,
  useWeeklyHoursEditor,
  type WeeklyHoursEditorState,
} from '@/features/opening-hours/components/WeeklyHoursEditor';
import {
  useClearOpeningHours,
  useOpeningHours,
  useSetOpeningHours,
} from '@/features/opening-hours/hooks/useOpeningHours';
import { useErrorT, useT } from '@/i18n';
import type { BulkOpeningHours, OpeningHours } from '@restaurant/api-contracts';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

type ChannelService = Exclude<BulkOpeningHours['service'], 'BASE'>;

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

function formatShifts(row: OpeningHours | undefined, closedLabel: string): string {
  if (!row || !row.isEnabled || row.shifts.length === 0) return closedLabel;
  return row.shifts.map((s) => `${s.open} – ${s.close}`).join(', ');
}

/** Read-only mirror of the base hours, shown while a channel follows them. */
function BasePreview({ rows }: { rows: OpeningHours[] }) {
  const t = useT();
  return (
    <>
      <BlockTitle>{t.openingHours.followingTheseHours}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {WEEK.map(({ dow }) => {
          const row = rows.find((r) => r.service === 'BASE' && r.dayOfWeek === dow);
          return (
            <ListItem key={dow} title={t.openingHours[DAY_I18N_KEY[dow]]}>
              <span
                slot="after"
                style={{
                  color: 'var(--f7-list-item-after-text-color)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatShifts(row, t.openingHours.closedBadge)}
              </span>
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

/** Per-service "follows the base hours" note. Kept here so the copy lives next
 * to where it renders and both host pages stay free of the lookup. */
function followNoteFor(service: ChannelService, t: ReturnType<typeof useT>): string {
  return {
    PICKUP: t.openingHours.pickupFollowNote,
    DELIVERY: t.openingHours.deliveryFollowNote,
    RESERVATION: t.openingHours.reservationFollowNote,
  }[service];
}

/**
 * The editable body of one channel's hours: the custom-hours toggle plus either
 * the weekly editor (custom) or a read-only mirror of the base hours (follow).
 * A channel either FOLLOWS the base hours (no rows of its own) or carries its
 * own full weekly schedule; the toggle flips between the two - off clears the
 * rows, on seeds a copy of the base hours to edit from.
 *
 * Extracted from the page shell so both the standalone channel page
 * (`ChannelHoursView`) and the combined Order Hours page (Pickup/Delivery tabs)
 * can host it. The `editor` is created by the host so its Navbar Save button and
 * this body share one source of truth.
 */
export function ChannelHoursContent({
  service,
  rows,
  editor,
}: {
  service: ChannelService;
  rows: OpeningHours[];
  editor: WeeklyHoursEditorState;
}) {
  const t = useT();
  const errorMessages = useErrorT();
  const setHours = useSetOpeningHours();
  const clearHours = useClearOpeningHours();

  const isCustom = rows.some((r) => r.service === service);
  const busy = setHours.isPending || clearHours.isPending;

  function enableCustom() {
    // Seed the new custom schedule from the current base hours so the operator
    // starts from "same as general" and tweaks from there.
    const body: BulkOpeningHours = {
      service,
      schedule: WEEK.map(({ dow }) => {
        const base = rows.find((r) => r.service === 'BASE' && r.dayOfWeek === dow);
        return {
          dayOfWeek: dow,
          isEnabled: base?.isEnabled ?? false,
          shifts: base?.isEnabled ? (base?.shifts ?? []) : [],
        };
      }),
    };
    setHours.mutate(body, {
      onError: (e) => toast(apiErrorText(e, errorMessages, t.openingHours.failedSetCustom)),
    });
  }

  function followBase() {
    // Clearing wipes the channel's whole custom weekly schedule - confirm first.
    // The toggle is controlled by `isCustom` (derived from rows), so on cancel it
    // stays visually "on" with no manual revert needed.
    f7.dialog.confirm(
      t.openingHours.confirmFollowMessage,
      t.openingHours.confirmFollowTitle,
      () => {
        clearHours.mutate(service, {
          onSuccess: () => toast(t.openingHours.nowFollowingGeneral),
          onError: (e) => toast(apiErrorText(e, errorMessages, t.openingHours.failedUpdate)),
        });
      },
    );
  }

  return (
    <>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.openingHours.setCustomHours}>
          <label className="toggle" slot="after">
            <input
              type="checkbox"
              checked={isCustom}
              disabled={busy}
              onChange={(e) => (e.target.checked ? enableCustom() : followBase())}
            />
            <span className="toggle-icon" />
          </label>
        </ListItem>
      </List>
      <BlockFooter>{followNoteFor(service, t)}</BlockFooter>

      {isCustom ? <WeeklyHoursEditor editor={editor} /> : <BasePreview rows={rows} />}
    </>
  );
}

/**
 * Standalone page for one channel's opening hours (used by Reservation hours).
 * Owns the Page/Navbar shell + Save action and delegates the body to
 * `ChannelHoursContent`.
 */
export function ChannelHoursView({ service }: { service: ChannelService }) {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: rows, isLoading, isError } = useOpeningHours();

  const title = {
    PICKUP: t.openingHours.pickupHoursTitle,
    DELIVERY: t.openingHours.deliveryHoursTitle,
    RESERVATION: t.openingHours.reservationHoursTitle,
  }[service];

  const isCustom = !!rows?.some((r) => r.service === service);
  const editor = useWeeklyHoursEditor(service, rows ?? []);

  return (
    <Page name="channel-hours">
      <Navbar title={title} backLink={settingsBackLink}>
        {!isLoading && !isError && rows && isCustom && (
          <SettingsSaveNav
            isDirty={editor.isDirty}
            saving={editor.isPending}
            onSave={editor.handleSave}
          />
        )}
      </Navbar>

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && (isError || !rows) && (
        <Block>
          <EmptyState
            icon="exclamationmark_triangle"
            title={t.openingHours.couldNotLoadHours}
            description={t.openingHours.checkConnection}
          />
        </Block>
      )}

      {!isLoading && !isError && rows && (
        <ChannelHoursContent service={service} rows={rows} editor={editor} />
      )}
    </Page>
  );
}
